'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { FormInput } from '@/components/forms/form-input';
import { FormCombobox } from '@/components/forms/form-combobox';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import {
  dynamicFormKeys,
  dynamicFormsApi,
  type DynamicForm
} from '@/features/dynamic-forms';
import { ApiError } from '@/lib/api/client';
import {
  useCreateOcrTemplateMutation,
  useGenerateOcrTemplateDescriptionMutation,
  useUpdateOcrTemplateMutation
} from '../services/ocr-templates.mutations';
import { createOcrTemplateSchema, updateOcrTemplateSchema } from '../schemas';
import type { OcrTemplateFormValues } from '../schemas';
import type { OcrTemplate } from '../types';

interface OcrTemplateFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: OcrTemplate | null;
}

const NAME_MAX = 60;
const DESC_MAX = 2000;

export function OcrTemplateFormDialog({
  open,
  onOpenChange,
  template
}: OcrTemplateFormDialogProps) {
  const t = useTranslations('ocrTemplates');
  const tv = useTranslations('ocrTemplates.validation');
  const isEditing = !!template;

  const createMutation = useCreateOcrTemplateMutation();
  const updateMutation = useUpdateOcrTemplateMutation();
  const generateDescriptionMutation =
    useGenerateOcrTemplateDescriptionMutation();

  const schema = useMemo(
    () =>
      isEditing ? updateOcrTemplateSchema(tv) : createOcrTemplateSchema(tv),
    [tv, isEditing]
  );

  const form = useForm<OcrTemplateFormValues>({
    resolver: zodResolver(schema) as any,
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      dynamic_form_id: '',
      is_active: true,
      // Never asked for in either dialog — the operator's question is "is my
      // document one record or many?", and the toggle asked it in extraction
      // vocabulary nobody could answer. Carried in form state only so an edit
      // round-trip preserves whatever the template already has (set through
      // template import/export), the same way `is_active` is carried here.
      repeats_per_page: true
    }
  });

  const nameValue = form.watch('name') ?? '';
  const descValue = form.watch('description') ?? '';

  // Load dynamic forms for the select dropdown (infinite scroll, never capped)
  const dynamicFormsSelect = useInfiniteSelect<DynamicForm>({
    queryKey: dynamicFormKeys.lists(),
    queryFn: (params) => dynamicFormsApi.getAll(params),
    mapOption: useCallback(
      (f: DynamicForm) => ({ value: String(f.id), label: f.name }),
      []
    )
  });

  useEffect(() => {
    if (open) {
      form.reset(
        template
          ? {
              name: template.name,
              description: template.description ?? '',
              dynamic_form_id:
                template.dynamic_form_id != null
                  ? String(template.dynamic_form_id)
                  : '',
              is_active: template.is_active,
              repeats_per_page: template.repeats_per_page ?? true
            }
          : {
              name: '',
              description: '',
              dynamic_form_id: '',
              is_active: true,
              repeats_per_page: true
            }
      );
    }
  }, [open, template, form]);

  async function onSubmit(values: OcrTemplateFormValues) {
    const formId = values.dynamic_form_id?.trim();
    const payload = {
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      dynamic_form_id: formId ? Number(formId) : null,
      is_active: values.is_active,
      repeats_per_page: values.repeats_per_page
    };

    try {
      if (isEditing && template) {
        await updateMutation.mutateAsync({ id: template.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ApiError && error.status === 422) {
        const fieldErrors = (
          error.data as { errors?: Record<string, string[]> }
        )?.errors;
        if (fieldErrors) {
          Object.entries(fieldErrors).forEach(([field, messages]) => {
            form.setError(field as Parameters<typeof form.setError>[0], {
              message: messages[0]
            });
          });
        }
      }
    }
  }

  async function handleGenerateDescription() {
    const name = form.getValues('name')?.trim() ?? '';
    if (name.length < 3) {
      toast.error(t('generateDescriptionNeedsName'));
      return;
    }

    const formIdRaw = form.getValues('dynamic_form_id')?.trim();
    const dynamicFormId = formIdRaw ? Number(formIdRaw) : null;

    try {
      const response = await generateDescriptionMutation.mutateAsync({
        name,
        dynamic_form_id: dynamicFormId,
        template_id: template?.id ?? null
      });

      const generated = response.data?.description ?? '';
      if (generated) {
        form.setValue('description', generated.slice(0, DESC_MAX), {
          shouldDirty: true,
          shouldValidate: true
        });
        toast.success(t('generateDescriptionSuccess'));
      }
    } catch {
      // ApiResponse::error already surfaced via global MutationCache.
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isGenerating = generateDescriptionMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='!grid-rows-[auto_1fr_auto] sm:max-w-[520px]'>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('editTemplate') : t('addTemplate')}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t('editDescription') : t('addDescription')}
          </DialogDescription>
        </DialogHeader>

        <Form
          form={form}
          onSubmit={form.handleSubmit(onSubmit)}
          className='min-h-0 overflow-y-auto'
        >
          <div className='grid gap-5 px-1 py-4'>
            {/* Template Name */}
            <div className='space-y-1'>
              <FormInput
                control={form.control}
                name='name'
                label={t('name')}
                placeholder={t('namePlaceholder')}
                required
              />
              <p
                className={cn(
                  'text-muted-foreground text-end text-xs',
                  nameValue.length > NAME_MAX && 'text-destructive'
                )}
              >
                {nameValue.length} / {NAME_MAX}
              </p>
            </div>

            {/* Description (single descriptive field — feeds AI suggestion) */}
            <div className='space-y-1'>
              <div className='flex items-center justify-between gap-2'>
                <label className='text-sm font-medium'>
                  {t('description')}
                </label>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  className='h-7 px-2 text-xs'
                  onClick={handleGenerateDescription}
                  disabled={isGenerating || isPending}
                >
                  {isGenerating ? (
                    <Loader2 className='me-1.5 h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <Sparkles className='me-1.5 h-3.5 w-3.5 text-amber-500' />
                  )}
                  {t('generateDescription')}
                </Button>
              </div>
              <textarea
                className={cn(
                  'border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[140px] w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                  form.formState.errors.description && 'border-destructive'
                )}
                placeholder={t('descriptionPlaceholder')}
                maxLength={DESC_MAX}
                {...form.register('description')}
              />
              <p className='text-muted-foreground text-xs'>
                {t('descriptionHelper')}
              </p>
              {form.formState.errors.description && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.description.message}
                </p>
              )}
              <p
                className={cn(
                  'text-muted-foreground text-end text-xs',
                  descValue.length > DESC_MAX && 'text-destructive'
                )}
              >
                {descValue.length} / {DESC_MAX}
              </p>
            </div>

            {/* Dynamic Form (optional, re-select to clear) */}
            <FormCombobox
              control={form.control}
              name='dynamic_form_id'
              label={t('dynamicForm')}
              placeholder={t('dynamicFormPlaceholder')}
              infiniteConfig={dynamicFormsSelect}
              displayValue={template?.dynamic_form?.name}
            />
          </div>
        </Form>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t('cancel')}
          </Button>
          <Button onClick={form.handleSubmit(onSubmit)} disabled={isPending}>
            {isPending && <Loader2 className='me-2 h-4 w-4 animate-spin' />}
            {isEditing ? t('save') : t('create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
